import { create } from 'zustand';
import getParameterFromURL from '../utils/scripts/getParameterFromURL';

const NUTRITION_ONBOARDING_CONFIGS = ['customV57', 'customV66'];
const VET_ONBOARDING_CONFIGS = new Set(['customV55', 'customV60']);
const PUPPY_BLUES_ONBOARDING_CONFIGS = new Set(['customV56', 'customV63', 'customV67', 'customV68']);
const POTTY_ONBOARDING_CONFIGS = new Set(['customV69', 'customV70', 'customV71']);

const initialConfig = getParameterFromURL('config') || 'customV18';

const getDerivedFlags = (config: string) => ({
  vetOnboarding: VET_ONBOARDING_CONFIGS.has(config),
  puppyBluesOnboarding: PUPPY_BLUES_ONBOARDING_CONFIGS.has(config),
});

export type StepItem = {
  name: string;
  showActionBar?: boolean;
};

export type StepsMeta = {
  separationStepIndexes: number[];
  hideHeaderStepIndexes: number[];
  hideProgressStepIndexes: number[];
  progressTitlesBySection: string[];
};

export type AppState = {
  // global
  showLoader: boolean;
  showSteps: boolean;
  initialAge: string;
  initialRole: string;
  forceLang: string | null;

  // steps
  currentStep: string | null;
  previousStep: string | null;
  nextStep: string | null;
  currentStepIndex: number | null;
  totalSteps: number | null;
  currentStepCompleted: boolean | null;
  showActionBar: boolean | null;

  list: StepItem[] | null;
  separationStepIndexes: number[];
  hideHeaderStepIndexes: number[];
  hideProgressStepIndexes: number[];
  progressTitlesBySection: string[];

  withoutMail: boolean;
  config: string;
  nutritionOnboarding: boolean;
  puppyOnboarding: boolean;
  pottyOnboarding: boolean;
  vetOnboarding: boolean;
  puppyBluesOnboarding: boolean;
  dogTranslator: boolean;
  k9Funnel: boolean;

  // actions
  toggleLoader: (v: boolean) => void;
  setAge: (age: string) => void;
  setRole: (role: string) => void;

  setSteps: (list: StepItem[]) => void;
  stepGoTo: (name: string) => void;
  setTotalSteps: (n: number) => void;
  setCurrentIndex: (n: number | null) => void;
  setPreviousStep: (n: string | null) => void;
  setNextStep: (n: string | null) => void;
  setCurrentStepCompleted: (s: boolean) => void;
  setShowActionBar: (s: boolean) => void;
  setStepsMeta: (meta: StepsMeta) => void;
  setPuppyOnboarding: (b: boolean) => void;
};

export const useAppStore = create<AppState>((set) => ({
  // global initial
  showLoader: false,
  showSteps: true,
  initialAge: 'adult',
  initialRole: 'haveDog',
  forceLang: getParameterFromURL('force_lang'),

  // steps initial
  currentStep: null,
  previousStep: 'age',
  nextStep: null,
  currentStepIndex: null,
  totalSteps: null,
  currentStepCompleted: null,
  showActionBar: null,

  list: null,
  separationStepIndexes: [],
  hideHeaderStepIndexes: [],
  hideProgressStepIndexes: [],
  progressTitlesBySection: [],

  withoutMail: true,
  config: initialConfig,
  nutritionOnboarding: NUTRITION_ONBOARDING_CONFIGS.includes(initialConfig),
  puppyOnboarding: false,
  pottyOnboarding: POTTY_ONBOARDING_CONFIGS.has(initialConfig),
  ...getDerivedFlags(initialConfig),
  dogTranslator: initialConfig === 'customV59',
  k9Funnel: initialConfig === 'customV76',

  // actions
  toggleLoader: (v) => set({ showLoader: v }),
  setAge: (age) => set({ initialAge: age }),
  setRole: (role) => set({ initialRole: role }),

  setSteps: (list) => set((s) => ({
    list,
    pottyOnboarding: POTTY_ONBOARDING_CONFIGS.has(s.config),
    nutritionOnboarding: NUTRITION_ONBOARDING_CONFIGS.includes(s.config),
    ...getDerivedFlags(s.config),
    dogTranslator: s.config === 'customV59',
    k9Funnel: s.config === 'customV76',
  })),
  stepGoTo: (name) => set({ currentStep: name }),
  setTotalSteps: (n) => set({ totalSteps: n }),
  setCurrentIndex: (n) => set({ currentStepIndex: n }),
  setPreviousStep: (n) => set({ previousStep: n }),
  setNextStep: (n) => set({ nextStep: n }),
  setCurrentStepCompleted: (s) => set({ currentStepCompleted: s }),
  setShowActionBar: (s) => set({ showActionBar: s }),
  setStepsMeta: (meta) => set(meta),
  setPuppyOnboarding: (b) => set({ puppyOnboarding: b }),
}));
